import React from 'react';
import { Link } from 'react-router-dom';
import { getAvatarUrl } from '../../utils/avatar';
import { FaTrophy, FaStar, FaHashtag } from 'react-icons/fa';

const RightSidebar = ({ stats, trendingTags, suggestedUsers, onFollow, potdWinner }) => (
  <aside className="hidden lg:block space-y-5 sticky top-6" data-testid="right-sidebar">
    {potdWinner && (
      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-200 rounded-2xl p-4 relative overflow-hidden" data-testid="potd-spotlight">
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-amber-200/40 to-transparent rounded-bl-full" />
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center shadow-sm">
            <FaTrophy className="text-white" size={13} />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700 font-arthaus">Today&apos;s Winner</h3>
            <p className="text-[10px] text-amber-500 font-medium font-arthaus">Point of the Day</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 mb-3">
          <Link to={`/profile/${potdWinner.author_username}`}>
            <img src={getAvatarUrl(potdWinner.author_avatar, potdWinner.author_name)} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-amber-300 shadow-md" />
          </Link>
          <div className="flex-1 min-w-0">
            <Link to={`/profile/${potdWinner.author_username}`} className="hover:underline">
              <p className="text-sm font-bold text-gray-900 truncate flex items-center space-x-1">
                <span>{potdWinner.author_name}</span>
                <FaTrophy className="text-amber-500" size={10} />
              </p>
            </Link>
            <p className="text-xs text-gray-500">@{potdWinner.author_username}</p>
          </div>
        </div>
        <p className="text-xs text-gray-800 line-clamp-3 leading-relaxed mb-3 font-medium">{potdWinner.content}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 text-xs">
            <span className="flex items-center space-x-1 text-amber-600 font-bold"><FaStar size={10} /><span>{potdWinner.votes} votes</span></span>
            {potdWinner.likes_count > 0 && <span className="text-gray-500">{potdWinner.likes_count} likes</span>}
          </div>
          {potdWinner.author_potd_wins > 1 && <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">{potdWinner.author_potd_wins}x</span>}
        </div>
      </div>
    )}

    {trendingTags.length > 0 && (
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Trending Now</h3>
          <Link to="/explore" className="text-xs text-[#4A90E2] hover:underline">See all</Link>
        </div>
        <div className="space-y-2.5">
          {trendingTags.map((tag) => (
            <Link key={tag.tag || tag.name || tag} to={`/hashtag/${tag.tag || tag.name || tag}`} className="flex items-center space-x-2.5 group">
              <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-[#4A90E2]/10 transition-colors">
                <FaHashtag className="text-gray-400 group-hover:text-[#4A90E2] transition-colors" size={11} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 group-hover:text-[#4A90E2] transition-colors">{tag.tag || tag.name || tag}</p>
                {tag.count && <p className="text-xs text-gray-400">{tag.count} points</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    )}

    {suggestedUsers.length > 0 && (
      <div className="bg-white border border-gray-100 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Who to Follow</h3>
          <Link to="/explore" className="text-xs text-[#4A90E2] hover:underline">More</Link>
        </div>
        <div className="space-y-3">
          {suggestedUsers.map((u) => (
            <div key={u.id} className="flex items-center space-x-3">
              <Link to={`/profile/${u.username}`}><img src={getAvatarUrl(u.avatar_url, u.full_name)} alt="" className="w-9 h-9 rounded-full object-cover" /></Link>
              <div className="flex-1 min-w-0">
                <Link to={`/profile/${u.username}`} className="hover:underline"><p className="text-sm font-semibold text-gray-900 truncate">{u.full_name}</p></Link>
                <p className="text-xs text-gray-400 truncate">@{u.username}</p>
              </div>
              <button onClick={() => onFollow(u.id)} data-testid={`follow-btn-${u.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all">Follow</button>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="bg-white border border-gray-100 rounded-2xl p-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Your Stats</h3>
      <div className="grid grid-cols-3 gap-3 text-center">
        <Link to="/profile" className="group">
          <p className="text-lg font-bold text-gray-900 group-hover:text-[#4A90E2] transition-colors">{stats.posts}</p>
          <p className="text-xs text-gray-400 font-arthaus">Points</p>
        </Link>
        <Link to="/profile" className="group">
          <p className="text-lg font-bold text-gray-900 group-hover:text-[#4A90E2] transition-colors">{stats.followers}</p>
          <p className="text-xs text-gray-400">Followers</p>
        </Link>
        <Link to="/profile" className="group">
          <p className="text-lg font-bold text-gray-900 group-hover:text-[#4A90E2] transition-colors">{stats.following}</p>
          <p className="text-xs text-gray-400">Following</p>
        </Link>
      </div>
    </div>
  </aside>
);

export default RightSidebar;
